import React, { useEffect, useState } from "react";
import { Loader2, ExternalLink, Download, FileText, CheckCircle2, Circle, Clock, AlertTriangle, Award, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const STATUS_META = {
  not_started: { label: "Not started", color: "bg-cream-200 text-foreground/60", icon: Circle },
  applied: { label: "Applied", color: "bg-marigold/30 text-deepred", icon: Clock },
  under_review: { label: "Under review", color: "bg-peacock/20 text-peacock", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  live: { label: "Live ✓", color: "bg-emerald-500 text-white", icon: Award },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

const STATUS_FLOW = ["not_started", "applied", "under_review", "approved", "live", "rejected"];

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.not_started;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${meta.color}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function PlatformCard({ p, onChange, onPdf, savingKey, downloadingKey }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(p.notes || "");
  const [status, setStatus] = useState(p.status || "not_started");
  const [appliedAt, setAppliedAt] = useState(p.applied_at || "");

  useEffect(() => {
    setNotes(p.notes || "");
    setStatus(p.status || "not_started");
    setAppliedAt(p.applied_at || "");
  }, [p.notes, p.status, p.applied_at]);

  const dirty = notes !== (p.notes || "") || status !== (p.status || "not_started") || appliedAt !== (p.applied_at || "");

  return (
    <div className="bg-white border border-cream-200 rounded-sm overflow-hidden" data-testid={`ota-card-${p.key}`}>
      <div className="p-5 border-b border-cream-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-serif text-xl text-deepred mb-1">{p.name}</div>
            <div className="flex items-center gap-3 text-xs text-foreground/65 flex-wrap">
              <span><strong className="text-foreground">Commission:</strong> {p.commission}</span>
              <span className="text-foreground/30">·</span>
              <span><strong className="text-foreground">Approval:</strong> {p.approval_time}</span>
              <span className="text-foreground/30">·</span>
              <span className="text-emerald-700 font-medium">{p.fee}</span>
            </div>
          </div>
          <StatusPill status={status} />
        </div>
        {p.notes && p.notes.includes("PERFECT fit") && (
          <div className="mt-3 text-xs text-emerald-700 italic">★ Best fit recommendation</div>
        )}
      </div>

      <div className="p-5 space-y-4 bg-cream-50">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-foreground/60 block mb-1.5">Application status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-white" data-testid={`ota-status-select-${p.key}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FLOW.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_META[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-foreground/60 block mb-1.5">Applied on (optional)</label>
            <input
              type="date"
              value={appliedAt ? appliedAt.slice(0, 10) : ""}
              onChange={(e) => setAppliedAt(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-cream-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40"
              data-testid={`ota-applied-date-${p.key}`}
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-foreground/60 block mb-1.5">Internal notes</label>
          <Textarea
            value={notes}
            rows={2}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="E.g. Sales contact: jane@getyourguide.com — discuss commission tier."
            className="bg-white"
            data-testid={`ota-notes-${p.key}`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(p.key, { status, notes, applied_at: appliedAt || null })}
            disabled={!dirty || savingKey === p.key}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-saffron hover:bg-deepred disabled:opacity-40 text-white text-xs uppercase tracking-wider rounded-sm transition-colors"
            data-testid={`ota-save-${p.key}`}
          >
            {savingKey === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save status
          </button>
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-peacock hover:bg-deepred text-white text-xs uppercase tracking-wider rounded-sm transition-colors"
            data-testid={`ota-portal-link-${p.key}`}
          >
            Open supplier portal <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            type="button"
            onClick={() => onPdf(p.key)}
            disabled={downloadingKey === p.key}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-saffron text-saffron hover:bg-saffron hover:text-white text-xs uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50"
            data-testid={`ota-download-kit-${p.key}`}
          >
            {downloadingKey === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download application kit (PDF)
          </button>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider text-foreground/60 hover:text-saffron transition-colors"
            data-testid={`ota-toggle-${p.key}`}
          >
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
            {expanded ? "Hide" : "Show"} requirements
          </button>
        </div>

        {expanded && (
          <div className="mt-2 pt-4 border-t border-cream-200 text-sm" data-testid={`ota-requirements-${p.key}`}>
            <div className="text-xs uppercase tracking-[0.2em] text-saffron mb-2 font-semibold">
              {p.name} requirements
            </div>
            <ul className="space-y-1.5">
              {p.requirements?.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-foreground/80">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            {p.notes && (
              <div className="mt-3 text-xs italic text-foreground/65 bg-marigold/10 border-l-2 border-saffron pl-3 py-2">
                {p.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminOtaDistribution() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/ota-platforms");
      setPlatforms(r.data);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load OTA platforms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const savePartnership = async (platform, payload) => {
    setSavingKey(platform);
    try {
      await api.put(`/admin/ota-partnerships/${platform}`, { platform, ...payload });
      toast.success(`${platform} updated`);
      await load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const downloadKit = async (platform) => {
    setDownloadingKey(platform);
    try {
      // Use direct fetch w/ auth header, then create blob download (since this isn't a form-driven GET)
      const token = localStorage.getItem("tti_token");
      const res = await fetch(`${BACKEND}/api/admin/ota/${platform}/application-kit.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tti-${platform}-application-kit.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Application kit downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloadingKey(null);
    }
  };

  const downloadCatalogCsv = async () => {
    setDownloadingCsv(true);
    try {
      const token = localStorage.getItem("tti_token");
      const res = await fetch(`${BACKEND}/api/admin/tours/catalog.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tti-tour-catalog.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tour catalog CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloadingCsv(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-cream-200 p-12 text-center" data-testid="ota-loading">
        <Loader2 className="w-5 h-5 animate-spin inline text-saffron" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ota-distribution-panel">
      {/* Header / instructions */}
      <div className="bg-gradient-to-r from-saffron/10 via-marigold/10 to-peacock/10 border border-saffron/30 rounded-sm p-6">
        <h2 className="font-serif text-2xl text-deepred mb-2">OTA distribution control center</h2>
        <p className="text-sm text-foreground/75 leading-relaxed max-w-3xl">
          Apply to each marketplace using the supplier portal, download the auto-generated application kit (cover letter + company profile + tour catalog summary),
          and track your status here. Once approved on any platform, your tours can be imported via the catalog CSV below.
        </p>
        <button
          onClick={downloadCatalogCsv}
          disabled={downloadingCsv}
          data-testid="download-catalog-csv"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-saffron hover:bg-deepred text-white text-sm uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50"
        >
          {downloadingCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Download tour catalog (CSV — importable into any OTA)
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ota-summary">
        {STATUS_FLOW.map((s) => {
          const count = platforms.filter((p) => p.status === s).length;
          const meta = STATUS_META[s];
          const Icon = meta.icon;
          return (
            <div key={s} className="bg-white border border-cream-200 p-3 text-center">
              <Icon className="w-4 h-4 mx-auto mb-1 text-foreground/50" />
              <div className="text-2xl font-serif">{count}</div>
              <div className="text-[10px] uppercase tracking-wider text-foreground/55 mt-0.5">{meta.label}</div>
            </div>
          );
        })}
      </div>

      {/* Platform cards */}
      <div className="grid lg:grid-cols-2 gap-4">
        {platforms.map((p) => (
          <PlatformCard
            key={p.key}
            p={p}
            onChange={savePartnership}
            onPdf={downloadKit}
            savingKey={savingKey}
            downloadingKey={downloadingKey}
          />
        ))}
      </div>

      {/* How to use */}
      <div className="bg-white border border-cream-200 p-6 rounded-sm">
        <h3 className="font-serif text-lg text-deepred mb-3">How to use this control center</h3>
        <ol className="space-y-2 text-sm text-foreground/80 list-decimal list-inside">
          <li>For each OTA below, click <strong>Download application kit (PDF)</strong> — this is your pre-written cover letter + company profile + tour catalog summary, customised per platform.</li>
          <li>Click <strong>Open supplier portal</strong> to go to the OTA&rsquo;s signup page and submit the application (attach the kit + the CSV catalog from above).</li>
          <li>Set the status to <strong>Applied</strong> and add a note with the contact person and date.</li>
          <li>When the OTA replies, move status to <strong>Under review</strong> / <strong>Approved</strong> / <strong>Live</strong>.</li>
          <li>Once <strong>Live</strong>, your tours will appear on that OTA. Bookings flow through them (they take commission, you receive the rest monthly).</li>
        </ol>
        <div className="mt-5 text-xs text-foreground/60 italic">
          <strong>Note:</strong> No agent can submit applications on your behalf — each OTA requires you to sign their supplier T&amp;Cs and provide tax/insurance docs personally.
        </div>
      </div>
    </div>
  );
}
